import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../components/BudgetPage.css';
import './BudgetRoute.css';
import {
  AppHeader,
  AuthModal,
  ServerManagementModal,
  SubscriptionModal,
  BetaModeModal,
  useServerManagement,
} from '../components';
import BudgetView from '../components/BudgetView';
import BudgetAllocationModal from '../components/modals/BudgetAllocationModal';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useBudgetMonth } from '../hooks/useBudgetMonth';
import { isFeatureEnabled } from '../utils/featureFlags';

export const BudgetRoute: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const subscription = useSubscription();
  const serverManagement = useServerManagement();
  const activeServer = serverManagement.getActiveServer();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [showServerManagement, setShowServerManagement] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaModalFeature, setBetaModalFeature] = useState('');
  const [pendingBetaAction, setPendingBetaAction] = useState<(() => void) | null>(null);
  const [showAllocationModal, setShowAllocationModal] = useState(false);

  const {
    selectedMonth,
    goToAdjacentMonth,
    canGoPrevious,
    canGoNext,
    budgetMonth,
    loading,
    error,
    refresh,
    patchCategoryBudget,
    replaceBudgetMonth
  } = useBudgetMonth({
    server: activeServer,
    enabled: Boolean(user && activeServer?.defaultBudgetId)
  });

  const handleShowAuthModal = (mode: 'signin' | 'signup' = 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleShowBetaModal = (featureName: string, onContinue?: () => void) => {
    setBetaModalFeature(featureName);
    setPendingBetaAction(() => onContinue);
    setShowBetaModal(true);
  };

  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    setBetaModalFeature('');
    setPendingBetaAction(null);
  };

  const handleContinueToFeature = () => {
    setShowBetaModal(false);
    setBetaModalFeature('');
    if (pendingBetaAction) {
      pendingBetaAction();
    }
    setPendingBetaAction(null);
  };

  const handleManageServers = () => {
    if (isFeatureEnabled('ENABLE_PAYWALL')) {
      if (!subscription.hasActiveSubscription) {
        setShowSubscriptionModal(true);
        return;
      }
    } else {
      handleShowBetaModal(t('title', { ns: 'budget' }), () => {
        setShowServerManagement(true);
      });
      return;
    }

    setShowServerManagement(true);
  };

  React.useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  const currency = activeServer?.defaultBudgetCurrency || 'USD';

  const renderContent = () => {
    if (!user) {
      return (
        <div className="budget-empty-state">
          <p>{t('errors.authRequired', { ns: 'budget' })}</p>
          <div className="budget-empty-actions">
            <button
              type="button"
              className="budget-action-btn"
              onClick={() => handleShowAuthModal('signin')}
            >
              {t('actions.signIn', { ns: 'budget' })}
            </button>
          </div>
        </div>
      );
    }

    if (!activeServer) {
      return (
        <div className="budget-empty-state">
          <p>{t('empty.noServer', { ns: 'budget' })}</p>
          <div className="budget-empty-actions">
            <button
              type="button"
              className="budget-action-btn"
              onClick={handleManageServers}
            >
              {t('actions.connectServer', { ns: 'budget' })}
            </button>
          </div>
        </div>
      );
    }

    if (!activeServer.defaultBudgetId) {
      return (
        <div className="budget-empty-state">
          <p>{t('empty.noBudget', { ns: 'budget' })}</p>
          <div className="budget-empty-actions">
            <button
              type="button"
              className="budget-action-btn"
              onClick={handleManageServers}
            >
              {t('actions.connectServer', { ns: 'budget' })}
            </button>
          </div>
        </div>
      );
    }

    if (loading && !budgetMonth) {
      return <div className="budget-loading">{t('loading', { ns: 'budget' })}</div>;
    }

    const errorMessage =
      error === 'AUTH_REQUIRED'
        ? t('errors.authRequired', { ns: 'budget' })
        : error === 'NEEDS_BUDGET_PASSWORD'
          ? t('errors.needsBudgetPassword', { ns: 'budget' })
          : error === 'MONTH_NOT_FOUND' || error === 'NO_BUDGET_MONTHS'
            ? t('errors.monthUnavailable', { ns: 'budget' })
            : error
              ? t('errors.loadFailed', { ns: 'budget' })
              : null;

    return (
      <>
        {errorMessage && <div className="budget-error">{errorMessage}</div>}
        {budgetMonth && activeServer && (
          <BudgetView
            budgetMonth={budgetMonth}
            currency={currency}
            selectedMonth={selectedMonth}
            server={activeServer}
            onPreviousMonth={() => goToAdjacentMonth(-1)}
            onNextMonth={() => goToAdjacentMonth(1)}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            onRefresh={refresh}
            onBudgetMonthReplace={replaceBudgetMonth}
            onCategoryBudgetPatch={patchCategoryBudget}
            onAllocate={() => setShowAllocationModal(true)}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <div className="App budget-route">
      <AppHeader
        user={user}
        activeServer={activeServer}
        onShowAuthModal={handleShowAuthModal}
        onManageServers={handleManageServers}
        currentPage="budget"
      />

      <main className="budget-route-main App-main">
        {renderContent()}
      </main>

      {activeServer?.defaultBudgetId && budgetMonth && (budgetMonth.toBudget ?? 0) > 0 && (
        <BudgetAllocationModal
          isVisible={showAllocationModal}
          onClose={() => setShowAllocationModal(false)}
          onApplied={refresh}
          server={activeServer}
          month={selectedMonth}
          currency={currency}
          toBudget={budgetMonth.toBudget ?? 0}
        />
      )}

      <AuthModal
        isVisible={showAuthModal}
        onClose={handleCloseAuthModal}
        initialMode={authMode}
      />

      <ServerManagementModal
        isVisible={showServerManagement}
        onClose={() => setShowServerManagement(false)}
        user={user}
        savedServers={serverManagement.savedServers}
        activeServerId={serverManagement.activeServerId}
        onSaveServers={serverManagement.saveServers}
        onSetActiveServer={serverManagement.setActiveServer}
        onRemoveServer={serverManagement.removeServer}
        onRemoveBudgetPassword={serverManagement.removeBudgetPassword}
        onLoadBudgetsForServer={serverManagement.loadBudgetsForServer}
        onValidateBudgetAccess={serverManagement.validateBudgetAccess}
      />

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSuccess={() => {
          setShowSubscriptionModal(false);
          subscription.refetch();
        }}
      />

      <BetaModeModal
        isVisible={showBetaModal}
        onClose={handleCloseBetaModal}
        onContinue={handleContinueToFeature}
        featureName={betaModalFeature}
      />
    </div>
  );
};
